import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { DateInput } from '@/components/DateInput.tsx';
import { Button } from '@/components/ui/button.tsx';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field.tsx';
import { Input } from '@/components/ui/input.tsx';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group.tsx';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { dateOfBirthSchema } from '@/lib/validation-schemas.ts';
import { genderOptions, recipientOptions } from '@/types/patient-info.type.ts';

const formId = 'patient-info-form';
const formSchema = z.object({
  recipient: z.enum(
    recipientOptions.map((option) => option.value),
    { message: 'Please make a selection' }
  ),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  gender: z.enum(
    genderOptions.map((option) => option.value),
    { message: 'Please make a selection' }
  ),
  dateOfBirth: dateOfBirthSchema,
});

type LegacyPatientInformationFormData = z.infer<typeof formSchema>;

interface PatientInformationFormProps {
  onSubmit: (data: LegacyPatientInformationFormData) => void;
}

const LegacyPatientInformationForm = ({ onSubmit }: PatientInformationFormProps) => {
  const { patientRecipient, patientFirstName, patientLastName, patientDateOfBirth, patientGender } =
    useSessionStore();

  const form = useForm<LegacyPatientInformationFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      recipient: patientRecipient || undefined,
      firstName: patientFirstName,
      lastName: patientLastName,
      dateOfBirth: patientDateOfBirth,
      gender: patientGender || undefined,
    },
  });

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} id={formId} className="space-y-8 sm:space-y-12">
      <FieldGroup>
        <Controller
          name="recipient"
          control={form.control}
          render={({ field, fieldState }) => (
            <FieldSet>
              <FieldLegend>Who are you seeking supplies for?</FieldLegend>
              <RadioGroup
                name={field.name}
                value={field.value ?? ''}
                onValueChange={field.onChange}
                aria-invalid={fieldState.invalid}
              >
                {recipientOptions.map((option) => (
                  <Field
                    orientation="horizontal"
                    data-invalid={fieldState.invalid}
                    key={option.value}
                    className="flex w-auto items-center gap-2"
                  >
                    <RadioGroupItem
                      value={option.value}
                      id={`${formId}-recipient-${option.value}`}
                      aria-invalid={fieldState.invalid}
                    />
                    <FieldLabel htmlFor={`${formId}-recipient-${option.value}`}>
                      {option.label}
                    </FieldLabel>
                  </Field>
                ))}
              </RadioGroup>
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </FieldSet>
          )}
        />

        <Controller
          name="firstName"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={`${formId}-firstName`}>Patient first name</FieldLabel>
              <Input {...field} id={`${formId}-firstName`} aria-invalid={fieldState.invalid} />
              {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="lastName"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={`${formId}-lastName`}>Patient last name</FieldLabel>
              <Input {...field} id={`${formId}-lastName`} aria-invalid={fieldState.invalid} />
              {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="dateOfBirth"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={`${formId}-dateOfBirth`}>Patient date of birth</FieldLabel>
              <DateInput
                {...field}
                id={`${formId}-dateOfBirth`}
                aria-invalid={fieldState.invalid}
              />
              {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="gender"
          control={form.control}
          render={({ field, fieldState }) => (
            <FieldSet>
              <FieldLegend>Patient sex</FieldLegend>
              <RadioGroup
                name={field.name}
                value={field.value ?? ''}
                onValueChange={field.onChange}
                aria-invalid={fieldState.invalid}
              >
                {genderOptions.map((option) => (
                  <Field
                    orientation="horizontal"
                    data-invalid={fieldState.invalid}
                    key={option.value}
                    className="flex w-auto items-center gap-2"
                  >
                    <RadioGroupItem
                      value={option.value}
                      id={`${formId}-gender-${option.value}`}
                      aria-invalid={fieldState.invalid}
                    />
                    <FieldLabel htmlFor={`${formId}-gender-${option.value}`}>
                      {option.label}
                    </FieldLabel>
                  </Field>
                ))}
              </RadioGroup>
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </FieldSet>
          )}
        />
      </FieldGroup>

      <p className="text-charcoal-500 text-xs">
        By continuing, you agree to be contacted by Conduit Health and have read and agree to these{' '}
        <a
          href="https://www.conduithealth.com/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs"
        >
          terms of service
        </a>{' '}
        and{' '}
        <a
          href="https://www.conduithealth.com/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs"
        >
          privacy policy
        </a>{' '}
        and{' '}
        <a
          href="https://www.conduithealth.com/hipaa-hms"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs"
        >
          HIPAA notice of privacy practices
        </a>
        .
      </p>

      <Button type="submit" className="w-full sm:w-auto sm:min-w-[186px]">
        Continue
      </Button>
    </form>
  );
};

export type { LegacyPatientInformationFormData };
export { LegacyPatientInformationForm };
